import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { UtilityService } from '../../../services/utility/utility.service';
import { Release, ReleaseStatus } from '../../../models/release';
import { TestPlan } from '../../../models/test-plan';
import { TestCase } from '../../../models/test-case';

export interface ReleaseUpdate {
  status: ReleaseStatus;
  openOnUtc: string | null;
  signOffDueOnUtc: string | null;
}

/**
 * Admin-only release configuration against the TestHub API.
 * Powers the Admin → Manage releases page (PBI 55094).
 */
@Injectable({ providedIn: 'root' })
export class AdminReleasesService {
  private utility = inject(UtilityService);

  private readonly releaseEndpoint = `${environment.testHubApiUrl}/v1/Release`;
  private readonly testPlanEndpoint = `${environment.testHubApiUrl}/v1/TestPlan`;
  private readonly testCaseEndpoint = `${environment.testHubApiUrl}/v1/TestCase`;

  getAll(): Observable<Release[]> {
    return this.utility.getRequest<Release[]>(this.releaseEndpoint);
  }

  /** Full release incl. suites → test plans (used to render the expanded plan chips). */
  getById(releaseId: number): Observable<Release> {
    return this.utility.getRequest<Release>(`${this.releaseEndpoint}/${releaseId}`);
  }

  /**
   * Admin configuration view: suites → all non-deleted plans regardless of live
   * state, each carrying isLive so toggled-off plans stay visible.
   */
  getConfiguration(releaseId: number): Observable<Release> {
    return this.utility.getRequest<Release>(`${this.releaseEndpoint}/${releaseId}/Configuration`);
  }

  /**
   * Import every suite under the release's root suite from Azure DevOps (plans +
   * cases). Existing Azure-backed suites/plans are synced so re-running the import
   * picks up newly added Azure content without changing existing Live toggles.
   */
  importFromAzure(releaseId: number, userId: number): Observable<Release> {
    return this.utility.postRequest<Release>(
      `${this.releaseEndpoint}/${releaseId}/Import/${userId}`,
      {},
    );
  }

  /** Update status + open / sign-off-due window. */
  update(releaseId: number, change: ReleaseUpdate, modifiedByUserId: number): Observable<Release> {
    return this.utility.putRequest<Release>(`${this.releaseEndpoint}/${releaseId}`, {
      ...change,
      modifiedByUserId,
    });
  }

  /** Azure-backed create (reuses the validated insert path). */
  create(
    releaseAzureId: number,
    releaseName: string,
    modifiedByUserId: number,
  ): Observable<Release> {
    return this.utility.postRequest<Release>(this.releaseEndpoint, {
      releaseAzureId,
      releaseName,
      modifiedByUserId,
    });
  }

  // ---- plans ----

  /** Turn a plan's tester visibility on or off. Only flips isLive. */
  setPlanLive(testPlanId: number, isLive: boolean, userId: number): Observable<TestPlan> {
    return this.utility.putRequest<TestPlan>(
      `${this.testPlanEndpoint}/${testPlanId}/Live/${isLive}/${userId}`,
      {},
    );
  }

  // ---- test cases ----

  getTestCases(testPlanId: number): Observable<TestCase[]> {
    return this.utility.getRequest<TestCase[]>(`${this.testCaseEndpoint}/${testPlanId}`);
  }

  /** All non-deleted cases under a plan regardless of live state (admin view). */
  getAllTestCases(testPlanId: number): Observable<TestCase[]> {
    return this.utility.getRequest<TestCase[]>(`${this.testCaseEndpoint}/${testPlanId}/All`);
  }

  /** Turn a case's tester visibility on or off. Only flips isLive. */
  setCaseLive(testCaseId: number, isLive: boolean, userId: number): Observable<TestCase> {
    return this.utility.putRequest<TestCase>(
      `${this.testCaseEndpoint}/${testCaseId}/Live/${isLive}/${userId}`,
      {},
    );
  }

}
